import { Divider, Group } from '@byecode/ui'
import { BACKGROUND_TYPE } from '@lighthouse/core'
import type { RichTextEditorProps } from '@lighthouse/shared'
import {
    DATE_SYSTEM,
    generatehtmlValue,
    getDateVariableOption,
    getFilterControllerVariableOption,
    getInputVariableOption,
    getIsFindUseInRichText,
    RichTextEditor,
    useAtomAction,
    useAtomData,
    useFindUseObjectContext
} from '@lighthouse/shared'
import React, { useCallback, useDeferredValue, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled, { css } from 'styled-components'

import {
    availableFieldBlocksAtom,
    availableFilterBlocksAtom,
    blockHighlightAtom,
    lastPageOfStackAtom,
    pageVariableSchemasFamily
} from '@/atoms/page/state'
import { AnchorSetting } from '@/components/AnchorSetting'
import { EventList } from '@/components/EventList'
import { DesignSetting, getBlockAndPageDesignLimit } from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { FontSetting } from '@/components/StyleSetting/FontSetting'
import { PositionSetting } from '@/components/StyleSetting/PositionSetting'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { VisibilitySetting } from '@/components/VisibilitySetting'
import { useBlockSettingContext } from '@/contexts/BlockSettingContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../Common/CursorSetting'
import { FloatingControl } from '../Common/FloatingControl'
import type { BlockSettingsSegmentedEnum } from '../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM } from '../constants'

const Container = styled.div<{ highlighting?: boolean }>`
    margin: 8px;
    border-radius: 8px;
    border: 1px solid var(--color-gray-200);
    overflow: hidden;
    .ProseMirror {
        min-height: 120px;
    }

    ${({ highlighting }) =>
        highlighting &&
        css`
            border-color: var(--color-main);
            box-shadow: 0 0 4px 0 var(--color-main);
            overflow: hidden;
        `}
`

const styleProps = getBlockAndPageDesignLimit('text')

export const TextBlockSetting: React.FC = () => {
    const { control, setValue } = useFormContext()

    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)

    const findUseObject = useFindUseObjectContext()
    const disabledWithVersion = useIsDisabledWithVersion()
    const { blockId } = useBlockSettingContext()

    /** ********************************* 富文本变量 start ********************************** */
    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const dateOption = useMemo(() => getDateVariableOption(DATE_SYSTEM), [])
    const userOption = useGetUserRecordOptions()
    const { customViewOption } = useVariableCustomViewOption()
    const pageVariableSchemas = useAtomData(pageVariableSchemasFamily(pageId))

    // input
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)
    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )
    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])
    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const inputOption = useMemo(() => {
        if (availableFieldBlocksInfo.length === 0) {
            return undefined
        }
        return getInputVariableOption(availableFieldBlocksInfo, dataSourceList, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        })
    }, [availableFieldBlocksInfo, dataSourceList, handleMouseEnter, handleMouseLeave])

    // filter
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    const filterOption = useMemo(() => {
        if (availableFilterBlocksInfo.length === 0) {
            return
        }
        return getFilterControllerVariableOption(availableFilterBlocksInfo, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        })
    }, [availableFilterBlocksInfo, handleMouseEnter, handleMouseLeave])

    const config: RichTextEditorProps['config'] = useMemo(
        () => ({
            mode: 'text-style',
            variable: {
                pageVariableSchemas,
                viewOptions: customViewOption ? [customViewOption] : undefined,
                inputOption,
                filterOption,
                pageOptions,
                userOption,
                dateOption,
                dataSources: dataSourceList
            }
        }),
        [customViewOption, dataSourceList, dateOption, filterOption, inputOption, pageOptions, pageVariableSchemas, userOption]
    )
    /** ********************************* 富文本变量 end ********************************** */

    return (
        <>
            <BlockConfigureSegmentedControl value={currentTab} onChange={setCurrentTab} />

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN && (
                <>
                    <Group label="内容">
                        <Controller
                            control={control}
                            name="content"
                            render={({ field }) => {
                                const isHighLight = getIsFindUseInRichText({
                                    doc: field.value,
                                    findUseObject
                                })
                                return (
                                    <Container highlighting={isHighLight}>
                                        <RichTextEditor
                                            disabled={disabledWithVersion}
                                            config={config}
                                            value={field.value}
                                            onChange={newJson => {
                                                field.onChange(newJson)
                                                setValue('htmlValue', generatehtmlValue(newJson))
                                            }}
                                            styles={{
                                                editorContent: {
                                                    backgroundColor: 'var(--color-gray-50)'
                                                }
                                            }}
                                        />
                                    </Container>
                                )
                            }}
                        />
                    </Group>
                    <Divider />
                    <PositionSetting {...styleProps.position} />
                    <Divider />
                    <SizeSetting {...styleProps.size} />
                    <Divider />
                    <FontSetting {...styleProps.font} />
                    <Divider />
                    <Group label="样式">
                        <DesignSetting
                            {...styleProps.design}
                            colorSetting={{
                                color: [BACKGROUND_TYPE.color, BACKGROUND_TYPE.gradient, BACKGROUND_TYPE.variable],
                                textBorder: [BACKGROUND_TYPE.color, BACKGROUND_TYPE.variable]
                            }}
                        />
                    </Group>
                    <Divider />
                    <Controller name="cursor" render={({ field }) => <CursorSetting {...field} />} />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE && (
                <>
                    <AnchorSetting />
                    <Divider />
                    <VisibilitySetting />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION && (
                <>
                    <Controller
                        name="eventList"
                        control={control}
                        render={({ field }) => <EventList blockType="text" value={field.value} onChange={field.onChange} />}
                    />
                    <Divider />
                    <Controller
                        name="floatPointerList"
                        control={control}
                        render={({ field }) => {
                            return <FloatingControl value={field.value} blockId={blockId} onChange={field.onChange} />
                        }}
                    />
                    <Divider />
                    <AnimationSetting />
                </>
            )}
        </>
    )
}
